/**
 * One policy document: its versions, the draft being written, and publishing.
 *
 * The version list is the history clients have been shown, oldest at the
 * bottom. A published version is read-only here exactly as it is in the
 * database — a trigger refuses any change to its text — and the page offers no
 * way to try. Writing changes happens in the one draft a document may have,
 * which starts as a copy of the latest text so an edit is an edit and not a
 * retyping.
 *
 * The preview uses the same renderer as the client apps, so what the author
 * previews is what clients read.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, Eye, Globe, Pencil, Plus, Save, Send, Trash2, Undo2 } from 'lucide-react';
import {
  ApiError,
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  DetailField,
  DetailGrid,
  DocumentText,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  StatusChip,
  Tabs,
  Textarea,
  api,
  cn,
  formatDate,
  formatDateTime,
  toast,
  useCan,
} from '@connect/web-shared';

import { DocumentFormDialog } from './document-form-dialog';
import { PublishVersionDialog } from './publish-version-dialog';
import {
  STATUS_TONES,
  fetchOptions,
  isScheduled,
  labelOf,
  legalKeys,
  type DocumentDetail,
  type VersionDetail,
  type VersionSummary,
} from './types';

function versionState(version: VersionSummary, now: Date): string {
  if (version.status === 'draft') return 'draft';
  if (isScheduled(version, now)) return 'scheduled';
  return version.is_current ? 'active' : 'retired';
}

const VERSION_LABELS: Record<string, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  active: 'In effect',
  retired: 'Replaced',
};

export function LegalDocumentPage() {
  const { publicId = '' } = useParams();
  const [search, setSearch] = useSearchParams();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);

  const options = useQuery({ queryKey: legalKeys.options, queryFn: fetchOptions, staleTime: Infinity });
  const document = useQuery({
    queryKey: legalKeys.document(publicId),
    queryFn: () => api.get<DocumentDetail>(`/legal/documents/${publicId}`),
  });

  const versions = document.data?.versions;
  const draft = versions?.find((v) => v.status === 'draft') ?? null;
  // The draft when there is one — that is what somebody opening the page came
  // to work on — otherwise the version clients see.
  const fallback = draft ?? versions?.find((v) => v.is_current) ?? versions?.[0] ?? null;
  // A version in the address that no longer exists (a discarded draft) falls
  // back rather than opening onto a 404.
  const asked = search.get('v');
  const selectedId =
    (asked && versions?.some((v) => v.public_id === asked) ? asked : null) ??
    fallback?.public_id ??
    null;
  const select = (id: string) =>
    setSearch(
      (params) => {
        params.set('v', id);
        return params;
      },
      { replace: true },
    );

  const newVersion = useMutation({
    mutationFn: () =>
      api.post<VersionDetail>(`/legal/documents/${publicId}/versions`, {}),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: legalKeys.all });
      select(created.public_id);
    },
    onError: (error) =>
      toast.error('Could not start a version', error instanceof ApiError ? error.message : undefined),
  });

  const retire = useMutation({
    mutationFn: () => api.post<DocumentDetail>(`/legal/documents/${publicId}/retire`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: legalKeys.all });
      toast.success('Document retired', 'Clients are no longer shown it.');
    },
    onError: (error) =>
      toast.error('Could not retire it', error instanceof ApiError ? error.message : undefined),
  });

  if (document.isLoading) return <LoadingState label="Loading the document" />;
  if (document.isError || !document.data) {
    return (
      <ErrorState
        message={document.error instanceof ApiError ? document.error.message : undefined}
        onRetry={() => void document.refetch()}
      />
    );
  }

  const doc = document.data;
  const active = doc.status === 'active';

  return (
    <div className="space-y-4">
      <PageHeader
        title={doc.title}
        description={doc.description ?? undefined}
        breadcrumbs={[
          { label: 'Legal' },
          { label: 'Policies', to: '/legal/documents' },
          { label: doc.title },
        ]}
        actions={
          <Can permission="core.legal_document.edit">
            {active ? (
              <>
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  <Pencil className="size-4" />
                  Edit details
                </Button>
                <Button variant="secondary" onClick={() => setRetiring(true)}>
                  <Archive className="size-4" />
                  Retire
                </Button>
              </>
            ) : null}
          </Can>
        }
      />

      <Card>
        <CardBody>
          <DetailGrid>
            <DetailField label="Key">
              <span className="font-mono text-xs">{doc.key}</span>
            </DetailField>
            <DetailField label="Kind">{labelOf(options.data?.document_kinds, doc.kind)}</DetailField>
            <DetailField label="Service">
              {labelOf(options.data?.services, doc.service) ?? 'Every service'}
            </DetailField>
            <DetailField label="Channel">{labelOf(options.data?.channels, doc.channel)}</DetailField>
            <DetailField label="Status">
              <StatusChip value={doc.status} tones={STATUS_TONES} />
            </DetailField>
            <DetailField label="Public">
              {doc.is_public ? (
                <span className="inline-flex items-center gap-1">
                  <Globe className="size-3.5" />
                  /legal/{doc.key}
                </span>
              ) : (
                'No'
              )}
            </DetailField>
          </DetailGrid>
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <Card className="self-start">
          <CardHeader
            title="Versions"
            actions={
              active && !draft ? (
                <Can permission="core.legal_document.edit">
                  <Button size="sm" onClick={() => newVersion.mutate()} loading={newVersion.isPending}>
                    <Plus className="size-3.5" />
                    New version
                  </Button>
                </Can>
              ) : null
            }
          />
          {versions?.length ? (
            <ul className="divide-y divide-line">
              {versions.map((version) => {
                const state = versionState(version, new Date());
                return (
                  <li key={version.public_id}>
                    <button
                      type="button"
                      onClick={() => select(version.public_id)}
                      className={cn(
                        'w-full space-y-0.5 px-4 py-2.5 text-left hover:bg-surface-2',
                        version.public_id === selectedId && 'bg-accent-subtle',
                      )}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-heading">{version.label}</span>
                        <StatusChip value={state} tones={STATUS_TONES} labels={VERSION_LABELS} />
                      </span>
                      <span className="block text-2xs text-faint">
                        {version.status === 'draft'
                          ? `Started ${formatDate(version.created_at)}`
                          : `From ${formatDate(version.effective_at)}${
                              version.requires_reconsent ? ' · asks again' : ''
                            }`}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <CardBody>
              <p className="text-sm text-muted">
                No versions yet. Start one to write the text.
              </p>
            </CardBody>
          )}
        </Card>

        {selectedId ? (
          <VersionPanel
            key={selectedId}
            versionId={selectedId}
            documentActive={active}
            isFirst={!versions?.some((v) => v.status === 'published')}
            currentLabel={versions?.find((v) => v.is_current)?.label ?? null}
          />
        ) : (
          <Card>
            <CardBody>
              <p className="text-sm text-muted">Start the first version to write this document.</p>
            </CardBody>
          </Card>
        )}
      </div>

      <DocumentFormDialog
        open={editing}
        onClose={() => setEditing(false)}
        document={doc}
        options={options.data}
      />
      <Confirm
        open={retiring}
        onClose={() => setRetiring(false)}
        onConfirm={() => retire.mutateAsync().then(() => undefined)}
        title={`Retire ${doc.title}?`}
        message="Clients are no longer shown it and it can publish no new version. Everything they accepted stays on record. A document an active requirement presents cannot be retired; retire the requirement first."
        confirmLabel="Retire"
      />
    </div>
  );
}

function VersionPanel({
  versionId,
  documentActive,
  isFirst,
  currentLabel,
}: {
  versionId: string;
  documentActive: boolean;
  isFirst: boolean;
  currentLabel: string | null;
}) {
  const queryClient = useQueryClient();
  const version = useQuery({
    queryKey: legalKeys.version(versionId),
    queryFn: () => api.get<VersionDetail>(`/legal/versions/${versionId}`),
  });
  const canEdit = useCan('core.legal_document.edit');

  const [label, setLabel] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [view, setView] = useState('write');
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [recalling, setRecalling] = useState(false);

  const loaded = version.data;
  useEffect(() => {
    if (!loaded) return;
    setLabel(loaded.label);
    setSummary(loaded.change_summary ?? '');
    setContent(loaded.content);
  }, [loaded]);

  const dirty = useMemo(
    () =>
      loaded !== undefined &&
      (label !== loaded.label ||
        summary !== (loaded.change_summary ?? '') ||
        content !== loaded.content),
    [loaded, label, summary, content],
  );

  const refresh = () => queryClient.invalidateQueries({ queryKey: legalKeys.all });

  const save = useMutation({
    mutationFn: () =>
      api.patch<VersionDetail>(`/legal/versions/${versionId}`, {
        label,
        change_summary: summary || null,
        content,
      }),
    onSuccess: async () => {
      await refresh();
      toast.success('Draft saved');
    },
    onError: (error) =>
      toast.error('Could not save the draft', error instanceof ApiError ? error.message : undefined),
  });

  const discard = useMutation({
    mutationFn: () => api.delete(`/legal/versions/${versionId}`),
    onSuccess: async () => {
      await refresh();
      toast.success('Draft discarded');
    },
    onError: (error) =>
      toast.error('Could not discard it', error instanceof ApiError ? error.message : undefined),
  });

  const recall = useMutation({
    mutationFn: () => api.post<VersionDetail>(`/legal/versions/${versionId}/unpublish`),
    onSuccess: async () => {
      await refresh();
      toast.success('Taken back into draft', 'Clients never saw it.');
    },
    onError: (error) =>
      toast.error('Could not take it back', error instanceof ApiError ? error.message : undefined),
  });

  if (version.isLoading) return <LoadingState label="Loading the version" />;
  if (version.isError || !loaded) {
    return (
      <ErrorState
        message={version.error instanceof ApiError ? version.error.message : undefined}
        onRetry={() => void version.refetch()}
      />
    );
  }

  const isDraft = loaded.status === 'draft';
  const scheduled = isScheduled(loaded, new Date());
  const editable = isDraft && canEdit && documentActive;

  return (
    <Card className="min-w-0">
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {loaded.label}
            {dirty ? <Badge tone="warning">Unsaved</Badge> : null}
          </span>
        }
        description={
          isDraft
            ? 'A draft: only staff can see it. Publish it to show clients.'
            : scheduled
              ? `Scheduled: takes effect ${formatDateTime(loaded.effective_at)}.`
              : `Published ${formatDateTime(loaded.published_at)}. It can no longer change.`
        }
        actions={
          isDraft ? (
            <>
              {editable ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label="Discard draft"
                    onClick={() => setDiscarding(true)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => save.mutate()}
                    loading={save.isPending}
                    disabled={!dirty || !label.trim()}
                  >
                    <Save className="size-3.5" />
                    Save
                  </Button>
                </>
              ) : null}
              {documentActive ? (
                <Can permission="core.legal_document.publish">
                  <Button
                    size="sm"
                    onClick={() => setPublishing(true)}
                    disabled={dirty || !loaded.content.trim()}
                    title={dirty ? 'Save the draft first' : undefined}
                  >
                    <Send className="size-3.5" />
                    Publish
                  </Button>
                </Can>
              ) : null}
            </>
          ) : scheduled && loaded.decisions === 0 ? (
            <Can permission="core.legal_document.publish">
              <Button variant="secondary" size="sm" onClick={() => setRecalling(true)}>
                <Undo2 className="size-3.5" />
                Take back
              </Button>
            </Can>
          ) : null
        }
      />
      <CardBody className="space-y-4">
        {isDraft && editable ? (
          <>
            <div className="grid gap-4 sm:grid-cols-[10rem_minmax(0,1fr)]">
              <Input
                label="Version label"
                required
                maxLength={40}
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
              <Input
                label="What changed"
                maxLength={2000}
                hint="Shown to clients beside an updated version."
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
              />
            </div>
            <Tabs
              value={view}
              onChange={setView}
              items={[
                { id: 'write', label: 'Write' },
                { id: 'preview', label: 'Preview' },
              ]}
            />
            {view === 'write' ? (
              <Textarea
                aria-label="Text"
                rows={24}
                className="font-mono text-xs"
                value={content}
                onChange={(event) => setContent(event.target.value)}
                hint="Headings with #, lists with - or 1., **bold**, _italic_, [links](https://…). Raw HTML is shown as text."
              />
            ) : content.trim() ? (
              <div className="rounded-card border border-line p-4">
                <DocumentText content={content} format={loaded.content_format} />
              </div>
            ) : (
              <p className="text-sm text-muted">Nothing written yet.</p>
            )}
            <Alert tone="warning" title="Wording is for your legal adviser">
              Connect stores and versions the text you write; it does not supply or review it.
              Have the wording approved before publishing.
            </Alert>
          </>
        ) : (
          <>
            <DetailGrid>
              <DetailField label="Effective">{formatDateTime(loaded.effective_at)}</DetailField>
              <DetailField label="Asks clients again">
                {loaded.requires_reconsent ? 'Yes' : 'No'}
              </DetailField>
              <DetailField label="Decisions on it">{String(loaded.decisions)}</DetailField>
              <DetailField label="SHA-256">
                <span className="font-mono text-2xs" title={loaded.content_sha256 ?? undefined}>
                  {loaded.content_sha256?.slice(0, 16) ?? '—'}
                </span>
              </DetailField>
            </DetailGrid>
            {loaded.change_summary ? (
              <p className="text-sm text-muted">
                <span className="font-medium text-body">What changed: </span>
                {loaded.change_summary}
              </p>
            ) : null}
            <div className="rounded-card border border-line p-4">
              {loaded.content.trim() ? (
                <DocumentText content={loaded.content} format={loaded.content_format} />
              ) : (
                <p className="flex items-center gap-2 text-sm text-muted">
                  <Eye className="size-4" /> No text.
                </p>
              )}
            </div>
          </>
        )}
      </CardBody>

      <PublishVersionDialog
        open={publishing}
        onClose={() => setPublishing(false)}
        version={loaded}
        isFirst={isFirst}
        currentLabel={currentLabel}
      />
      <Confirm
        open={discarding}
        onClose={() => setDiscarding(false)}
        onConfirm={() => discard.mutateAsync().then(() => undefined)}
        title="Discard this draft?"
        message="The draft's text is deleted. Published versions are not affected."
        confirmLabel="Discard"
      />
      <Confirm
        open={recalling}
        onClose={() => setRecalling(false)}
        onConfirm={() => recall.mutateAsync().then(() => undefined)}
        title={`Take ${loaded.label} back?`}
        message="It returns to draft before taking effect. Nobody has seen it, so nobody is affected."
        confirmLabel="Take back"
        variant="primary"
      />
    </Card>
  );
}
